import { styled } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { useI18n } from '@app-velocity/i18n';
import { RemoveIcon } from '@app-velocity/icons';
import { Form, Select, Typography } from '@app-velocity/ui';

import {
  isPersonaSupportedLanguage,
  PERSONA_SUPPORTED_LANGUAGES,
  PersonaSupportedLanguageCode,
} from '../../../../../../../../constants';
import {
  PersonaFormValues,
 LANGUAGE_UNDERSTANDING_LEVELS } from '../../../../types/persona';

const StyledSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledLanguageRow = styled('li')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

const StyledLanguageList = styled('ul')(({ theme }) => ({
  padding: 0,
  margin: 0,
  listStyleType: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledDeleteButton = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  padding: 0,
  svg: {
    fill: 'var(--error-error-color)',
  },
});

const StyledAddLanguageRow = styled(Form.FieldContainer)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const LanguageProficiencySection = () => {
  const { t } = useI18n();
  const { control, watch } = useFormContext<PersonaFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'languages',
  });

  const selectedLanguages = watch('languages');
  const availableLanguages = PERSONA_SUPPORTED_LANGUAGES.filter(
    (languageCode) =>
      !selectedLanguages.some(
        (entry) => entry.language === languageCode
      )
  );

  const addLanguage = (languageCode: PersonaSupportedLanguageCode) => {
    append({ language: languageCode, understandingLevel: 'FLUENT' });
  };

  return (
    <StyledSection>
      <Form.LabelWithTooltip tooltip={t('personas:form.tooltips.languages')}>
        {t('personas:form.languages')}
      </Form.LabelWithTooltip>

      <StyledLanguageList>
      {fields.map((field, index) => {
        const languageCode = selectedLanguages[index]?.language;

        if (!languageCode || !isPersonaSupportedLanguage(languageCode)) {
          return null;
        }

        return (
          <StyledLanguageRow key={field.id}>
            <Typography>
              {t(`personas:language.${languageCode}`)}
            </Typography>
            <Controller
              name={`languages.${index}.understandingLevel`}
              control={control}
              render={({ field: levelField }) => (
                <Select
                  MenuProps={{ disablePortal: true }}
                  value={levelField.value}
                  onChange={(event) =>
                    levelField.onChange(
                      event.target
                        .value as PersonaFormValues['languages'][number]['understandingLevel']
                    )
                  }
                >
                  {LANGUAGE_UNDERSTANDING_LEVELS.map((level) => (
                    <Select.Item key={level} value={level}>
                      {t(`personas:understanding_level.${level}`)}
                    </Select.Item>
                  ))}
                </Select>
              )}
            />
            <StyledDeleteButton
              type="button"
              aria-label={t('personas:form.remove_language')}
              onClick={() => remove(index)}
            >
              <RemoveIcon />
            </StyledDeleteButton>
          </StyledLanguageRow>
        );
      })}
</StyledLanguageList>
      {availableLanguages.length > 0 ? (
        <StyledAddLanguageRow>
          <Select
            MenuProps={{ disablePortal: true }}
            displayEmpty
            value=""
            renderValue={() => t('personas:form.select_language')}
            onChange={(event) => {
              const languageCode = String(event.target.value);

              if (isPersonaSupportedLanguage(languageCode)) {
                addLanguage(languageCode);
              }
            }}
          >
            {availableLanguages.map((languageCode) => (
              <Select.Item key={languageCode} value={languageCode}>
                {t(`personas:language.${languageCode}`)}
              </Select.Item>
            ))}
          </Select>
        </StyledAddLanguageRow>
      ) : null}
    </StyledSection>
  );
};
